import React, { useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

import {
  Divider,
  EmptyState,
  InpockView,
  Paragraph1,
  Paragraph2,
  SmallText,
} from 'inpock-ui-react';
import dayjs from 'dayjs';

import {
  Icon,
  PageTitle,
  SearchInput,
  Thumbnail,
  Skeleton,
  GroupPurchaseBanner,
} from '@/components';
import { useQueryDebounce, useOrderGroupsQuery } from '@/hooks';
import { OrderGroup } from '@/apis';
import { thumbnail } from '@/utils';
import { ORDER_STATUS } from '@/constants';

import * as S from './styled';

function OrderGroupItem({ orderGroup }: { orderGroup: OrderGroup }) {
  const navigate = useNavigate();
  const onClickOrder = (order_group_number: string) => {
    navigate(`/customer/orders/${order_group_number}`);
  };

  return (
    <S.OrderGroupContainer>
      <S.OrderGroupHeader>
        <div className="date-container">
          <Paragraph1>{dayjs(orderGroup.created_at).format('YYYY-MM-DD')}</Paragraph1>
        </div>
        <div className="detail-btn" onClick={() => onClickOrder(orderGroup.order_group_number)}>
          <Paragraph2 className="mr-4 text-gray-600">상세보기</Paragraph2>
          <Icon icon="ArrowRight" size={12} color="#979797" />
        </div>
      </S.OrderGroupHeader>
      <S.OrderGroupContent>
        <S.StatusBadge>
          <SmallText style={{ fontWeight: 'bold', color: '#FF5B1A' }}>
            {ORDER_STATUS[orderGroup.status]}
          </SmallText>
        </S.StatusBadge>
        <S.OrderGroupBody>
          <div className="order-group-image">
            <Thumbnail src={thumbnail.small(orderGroup.image)} alt={orderGroup.name} />
            <div className="image-count">
              <SmallText className="text-white">{orderGroup.orderline_count}</SmallText>
            </div>
          </div>
          <div className="order-group-info">
            <div className="d-flex align-items-center" style={{ width: '100%', gap: '5px' }}>
              <Paragraph2 className="order-group-name" style={{ fontWeight: 'bold' }}>
                {orderGroup.name}
              </Paragraph2>
              <div style={{ minWidth: '36px' }}>
                <Paragraph2
                  className="text-orange order-group-count"
                  style={{ fontWeight: 'bold' }}
                >
                  총 {orderGroup.orderline_count}건
                </Paragraph2>
              </div>
            </div>
            <SmallText>
              총 <span className="text-bold">{orderGroup.total_price.toLocaleString()}원</span>
            </SmallText>
          </div>
        </S.OrderGroupBody>
      </S.OrderGroupContent>
    </S.OrderGroupContainer>
  );
}

export function CustomerOrdersPage() {
  const [keyword, setKeyword] = useState<string>('');
  const debouncedKeyword = useQueryDebounce(keyword, 500);
  const { data: orderGroups, isFetching } = useOrderGroupsQuery(debouncedKeyword);
  const dataFetching = isFetching || debouncedKeyword !== keyword;

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>주문내역 | 인포크링크</title>
        </Helmet>
      </HelmetProvider>
      <InpockView>
        <div className="container">
          <PageTitle title="주문내역" />
          <section>
            <Divider spacing={[0, 20]} />
            <SearchInput
              value={keyword}
              setValue={setKeyword}
              placeholder="상품명으로 검색해보세요."
            />
            <Divider wide spacing={20} />
            <div className="ml-4 mr-4">
              <div>
                {dataFetching ? (
                  <>
                    {new Array(3).fill(0).map((_, index) => (
                      <Skeleton key={index} height={80} />
                    ))}
                  </>
                ) : orderGroups?.length === 0 ? (
                  <EmptyState
                    height={400}
                    className="mt-30"
                    emojii="😥"
                    mainText={debouncedKeyword ? '검색 결과가 없어요' : '주문 내역이 없어요'}
                    subText={
                      debouncedKeyword
                        ? '다른 검색어로 검색해주세요.'
                        : '인포크링크를 통해 더 쉽게 상품을 구매해보세요'
                    }
                  />
                ) : (
                  orderGroups?.map((orderGroup, index) => (
                    <React.Fragment key={index}>
                      <OrderGroupItem orderGroup={orderGroup} />
                      <Divider wide spacing={20} />
                      {index === 2 && (
                        <>
                          <GroupPurchaseBanner utmContent="orders" />
                          <Divider wide spacing={20} />
                        </>
                      )}
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>
          </section>
        </div>
      </InpockView>
    </>
  );
}
